import { Collapse, MenuItem, TextField, Tooltip } from "@material-ui/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaShip } from "react-icons/fa";
import {
  FiArrowRight,
  FiCalendar,
  FiChevronDown,
  FiExternalLink,
  FiEye,
  FiEyeOff,
  FiInbox,
  FiX
} from "react-icons/fi";

import CustomTabs from "../../../common/Tabs";
import CustomPagination from "../../../components/Common/CustomPagination";
import { getOrganizationId } from "../../../utils";
import { getCargoVoyageList, getVoyageList } from "./action";
import "./VoyageList.css";

const initialPagination = {
  offset: 1,
  limit: 20,
  totalPages: 0,
  totalItems: 0,
};

const hasRoute = (trip) => {
  if (!Array.isArray(trip?.routeDetails?.ports)) return false;
  if (trip?.contractType === "cargo-cargo") {
    const validPorts = trip.routeDetails.ports.filter(
      (p) => !(p.operation?.toLowerCase() === "commence" && !p.port)
    );
    return validPorts.length > 0;
  }
  return trip.routeDetails.ports.length > 0;
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

const formatCurrency = (val) => {
  if (val == null || isNaN(val)) return "—";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(val);
};

const splitVoyageNumber = (vn) => {
  if (!vn) return { voyage: "—", trip: "—" };
  const tIdx = vn.indexOf("T");
  if (tIdx !== -1) {
    return {
      voyage: vn.substring(0, tIdx),
      trip: vn.substring(tIdx),
    };
  }
  const sIdx = vn.indexOf("S");
  if (sIdx !== -1) {
    return {
      voyage: vn.substring(0, sIdx),
      trip: vn.substring(sIdx),
    };
  }
  return { voyage: vn, trip: "—" };
};

const getContractRoute = (type, id) => {
  if (!id) return "#";
  if (type === "vessel-vessel") return `/vessel-vessel-calc/${id}`;
  if (type === "vessel-cargo") return `/operation-calc/${id}`;
  if (type === "cargo-cargo") return `/cargo-cargo-calc/${id}`;
  return "#";
};

const opStyles = (op = "") => {
  const o = op.toLowerCase();
  if (o.includes("commence"))
    return {
      text: "text-[#313C4C]",
      bg: "bg-[#313C4C]",
      border: "border-[#313C4C]",
    };
  if (o.includes("loading"))
    return {
      text: "text-[#1e40af]",
      bg: "bg-[#1e40af]",
      border: "border-[#1e40af]",
    };
  if (o.includes("discharg"))
    return {
      text: "text-[#15803d]",
      bg: "bg-[#15803d]",
      border: "border-[#15803d]",
    };
  if (o.includes("bunker"))
    return {
      text: "text-[#d97706]",
      bg: "bg-[#d97706]",
      border: "border-[#d97706]",
    };
  if (o.includes("delivery") || o.includes("redelivery"))
    return {
      text: "text-[#7c3aed]",
      bg: "bg-[#7c3aed]",
      border: "border-[#7c3aed]",
    };
  return { text: "text-[#000]", bg: "bg-[#000]", border: "border-[#000]" };
};

const RouteDetailPanel = ({ ports = [] }) => {
  if (!ports?.length) return null;
  return (
    <div className="px-3 py-2.5 bg-[#f8fafc]">
      <p className="mb-2.5 text-[10.5px] text-[#000] capitalize tracking-wider">
        Full Route Overview — {ports.length} port{ports.length !== 1 ? "s" : ""}
      </p>
      <div className="flex items-start flex-wrap gap-y-4">
        {ports.map((p, i) => {
          const isFirst = i === 0;
          const isLast = i === ports.length - 1;
          const { text, bg, border } = opStyles(p.operation);

          return (
            <div key={i} className="flex items-center">
              <div className="flex flex-col items-center gap-1.5 min-w-[80px]">
                <div className="flex items-center gap-2">
                  <div
                    className={`shrink-0 rounded-full border-2 
                    ${isFirst
                        ? "w-2.5 h-2.5 bg-[#313C4C] border-[#313C4C]"
                        : isLast
                          ? "w-2.5 h-2.5 bg-[#15803d] border-[#15803d]"
                          : `w-2 h-2 ${bg} ${border}`
                      }`}
                  />
                  <span
                    className={`text-xs whitespace-nowrap text-[#000] ${isFirst || isLast ? "" : ""}`}
                  >
                    {p.port || "—"}
                  </span>
                </div>
                <span
                  className={`text-[9.5px]  capitalize tracking-wider whitespace-nowrap ${text}`}
                >
                  {p.operation || "—"}
                </span>
              </div>
              {!isLast && (
                <span className="text-[#cbd5e1] mx-2 pb-5 shrink-0">
                  <FiArrowRight size={13} />
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TripRow = ({ trip, isLast }) => {
  const [routeOpen, setRouteOpen] = useState(false);
  const route = trip?.routeDetails;
  let ports = route?.ports || [];
  if (trip?.contractType === "cargo-cargo") {
    ports = ports.filter(
      (p) => !(p.operation?.toLowerCase() === "commence" && !p.port)
    );
  }
  const start = trip?.contractType === "cargo-cargo"
    ? (ports[0]?.port || "—")
    : (route?.startingPort || ports[0]?.port || "—");
  const end = route?.endingPort || ports[ports.length - 1]?.port || "—";
  const qty = trip?.cargoSummary?.totalCargoQty ?? 0;
  const count = trip?.cargoSummary?.cargoCount ?? 0;
  const hasFullRoute = ports.length > 1;
  const tripStartDate = formatDate(trip?.tripStartDate);
  const tripEndDate = formatDate(trip?.tripEndDate);
  const contractLink = getContractRoute(
    trip?.contractType,
    trip?.estimationContractId,
  );

  return (
    <>
      <tr
        className={`transition-all duration-200 ${!routeOpen ? "hover:bg-[#f8fafc] cursor-pointer bg-white" : "bg-[#f8fafc]"} 
        ${isLast && !routeOpen ? "border-b-0" : "border-b border-[#e2e8f0]"}`}
        onClick={(e) => {
          e.stopPropagation();
          setRouteOpen((p) => !p);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (contractLink && contractLink !== "#") {
            window.open(contractLink, "_blank");
          }
        }}
      >
        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap max-w-[180px] border-r border-gray-300">
          {trip?.contractName ? (
            <Tooltip title={trip.contractName} placement="top">
              <a
                href={contractLink}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-1.5 text-[11px] font-medium text-blue-600 hover:text-blue-800 truncate transition-colors focus:outline-none"
              >
                <span className="truncate group-hover:underline">
                  {trip.contractName}
                </span>
                <FiExternalLink size={11} className="shrink-0 opacity-70" />
              </a>
            </Tooltip>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300 text-center">
          <span className="text-[11px] font-medium text-gray-800 tracking-tight">
            {splitVoyageNumber(trip?.voyageNumber).trip}
          </span>
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300">
          <span className="text-[11px] font-medium text-gray-800">{start}</span>
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium text-gray-800">{end}</span>
            {hasFullRoute && (
              <span className="text-[10px] text-gray-600 bg-gray-100 border border-gray-300 rounded-none px-1 py-[1px]">
                +{ports.length - 2} stops
              </span>
            )}
          </div>
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-800">
            <FiCalendar size={11} className="text-gray-500" />
            {tripStartDate || "—"}
          </div>
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-gray-800">
            <FiCalendar size={11} className="text-gray-500" />
            {tripEndDate || "—"}
          </div>
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300">
          <span className="text-[11px] font-medium text-gray-800 capitalize">
            {trip?.contractType || "—"}
          </span>
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300 text-right">
          <span className="text-[11px] font-medium text-gray-800 tabular-nums">
            {qty.toLocaleString()}
          </span>
          <span className="text-[11px] font-medium text-gray-500"> MT</span>
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300 text-center">
          <span className="text-[11px] font-medium text-gray-800">
            {count} unit{count !== 1 ? "s" : ""}
          </span>
        </td>

        <td className="!py-0.5 !px-2 align-middle text-right whitespace-nowrap border-r border-gray-300">
          {trip.profit !== undefined && trip.profit !== null ? (
            <span
              className={`text-[11px] font-medium tabular-nums tracking-tight ${
                trip.profit < 0
                  ? "text-rose-600"
                  : trip.profit > 0
                  ? "text-emerald-600"
                  : "text-gray-800"
              }`}
            >
              {formatCurrency(trip.profit)}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap text-center border-r border-gray-300">
          <span className="text-[11px] font-medium text-gray-800 capitalize">
            {trip?.status || "—"}
          </span>
        </td>

        <td className="!py-0.5 !px-2 align-middle w-12 text-center">
          {hasFullRoute ? (
            <Tooltip
              title={routeOpen ? "Hide full route" : "View full route"}
              placement="top"
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setRouteOpen((p) => !p);
                }}
                className={`w-4 h-4 mx-auto rounded-md flex items-center justify-center transition-all duration-200 shrink-0 outline-none`}
              >
                {routeOpen ? <FiEyeOff size={12} /> : <FiEye size={12} />}
              </button>
            </Tooltip>
          ) : (
            <span className="inline-block w-8" />
          )}
        </td>
      </tr>

      {hasFullRoute && routeOpen && (
        <tr className="bg-[#f8fafc] detail-row-inner">
          <td
            colSpan={12}
            className="p-0 border-b border-[#e2e8f0]"
          >
            <Collapse in={routeOpen} timeout={250} unmountOnExit>
              <RouteDetailPanel ports={ports} />
            </Collapse>
          </td>
        </tr>
      )}
    </>
  );
};

const VesselRow = ({ vessel, showDivider, idx }) => {
  const [open, setOpen] = useState(false);
  const name = vessel?.vesselDetails?.vesselName || "Unknown Vessel";
  const port = vessel?.vesselDetails?.port || "—";
  const hire = vessel?.vesselDetails?.hirePerDay ?? 0;
  const gbb = vessel?.vesselDetails?.gbb ?? 0;
  const allTrips = vessel?.trips || [];
  const trips = allTrips.filter(hasRoute);
  const tripCount = trips.length;
  const totalQty = trips.reduce(
    (a, t) => a + (t?.cargoSummary?.totalCargoQty || 0),
    0,
  );

  const totalProfit = trips.reduce((a, t) => a + (t?.profit || 0), 0);
  const voyageNumberPart = splitVoyageNumber(allTrips[0]?.voyageNumber).voyage;

  return (
    <>
      <tr
        onClick={() => setOpen((p) => !p)}
        className={`cursor-pointer h-6 hover:bg-gray-50 border-b border-gray-200 ${
          open ? "bg-[#f0f6ff]" : idx % 2 === 0 ? "bg-white" : "bg-[#f8fafc]"
        }`}
      >
        <td
          className="!py-0.5 !px-2 text-center align-middle border-r border-gray-300"
          style={{ width: "25px", minWidth: "25px", maxWidth: "25px" }}
        >
          <FiChevronDown
            size={14}
            className={`transition-transform duration-300 shrink-0 mx-auto ${open ? "rotate-180" : ""}`}
          />
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300">
          <div className="text-[11px] font-medium text-gray-800 tracking-tight truncate max-w-[180px]">
            {name}
          </div>
        </td>

        <td className="!py-0.5 !px-2 text-center align-middle whitespace-nowrap border-r border-gray-300">
          <span className="text-[11px] font-medium text-gray-800 tracking-tight">
            {voyageNumberPart}
          </span>
        </td>

        <td className="!py-0.5 !px-2 text-center align-middle whitespace-nowrap border-r border-gray-300">
          {tripCount > 0 ? (
            <span className="text-[11px] font-medium text-gray-800 tabular-nums">
              {tripCount}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 text-right align-middle whitespace-nowrap border-r border-gray-300">
          {totalQty > 0 ? (
            <span className="text-[11px] font-medium text-gray-800 tabular-nums">
              {totalQty.toLocaleString()}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 text-right align-middle whitespace-nowrap border-r border-gray-300">
          {hire > 0 ? (
            <span className="text-[11px] font-medium text-gray-800 tabular-nums">
              {hire.toLocaleString()}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 text-right align-middle whitespace-nowrap border-r border-gray-300">
          {totalProfit !== 0 ? (
            <span
              className={`text-[11px] font-medium tabular-nums tracking-tight ${
                totalProfit < 0 ? "text-rose-600" : "text-emerald-600"
              }`}
            >
              {formatCurrency(totalProfit)}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 text-right align-middle whitespace-nowrap">
          {gbb > 0 ? (
            <span className="text-[11px] font-medium text-amber-600 tabular-nums">
              {gbb.toLocaleString()}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

      </tr>

      {open && (
        <tr className="bg-[#f4f7fa] detail-row-outer">
          <td colSpan={8} className="!p-0 border-none bg-[#f4f7fa]">
            <Collapse in={open} timeout={300} unmountOnExit>
            <div className="bg-[#f4f7fa] overflow-x-auto p-3 md:p-4">
              {tripCount === 0 ? (
                <div className="p-3 flex items-center justify-center text-center gap-3 bg-white rounded-none border border-slate-300 shadow-sm">
                  <div className="w-8 h-8 rounded-none bg-slate-100 flex items-center justify-center shrink-0">
                    <FiInbox size={16} className="text-[#000]" />
                  </div>
                  <div>
                    <p className="m-0 text-[11px] text-[#000]">
                      No active routes
                    </p>
                    <p className="m-0 mt-0.5 text-[11px] text-slate-500">
                      {allTrips.length > 0
                        ? `${allTrips.length} trip(s) recorded, but none have valid route data assigned yet.`
                        : "There are currently no trips assigned to this vessel."}
                    </p>
                  </div>
                </div>
              ) : (
                /* The floating nested table Card */
                <div className="rounded-none border border-slate-300 bg-white overflow-hidden shadow-sm">
                  <table className="w-full border-collapse min-w-[850px]">
                    <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                      <tr>
                        {[
                          { label: "Contract Name" },
                          { label: "Trip Number", align: "text-center" },
                          { label: "Start Port" },
                          { label: "End Port" },
                          { label: "Start Date" },
                          { label: "End Date" },
                          { label: "Contract Type" },
                          { label: "Cargo Qty", align: "text-right" },
                          { label: "Cargo Units", align: "text-center" },
                          { label: "Profit", align: "text-right" },
                          { label: "Status", align: "text-center" },
                          { label: "Actions", align: "text-center" },
                        ].map((h, i) => (
                          <th
                            key={i}
                            className={`!py-0.5 !px-2 ${h.align || "text-left"
                              } text-[11px] font-medium text-[#777777] capitalize tracking-wider whitespace-nowrap ${
                                i < 11 ? "border-r border-gray-300" : ""
                              }`}
                          >
                            {h.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {trips.map((trip, idx) => (
                        <TripRow
                          key={trip?.estimationContractId ?? idx}
                          trip={{ ...trip, tripIndex: idx }}
                          isLast={idx === trips.length - 1}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Collapse>
        </td>
      </tr>
      )}
    </>
  );
};

const CargoRow = ({ cargo, showDivider, idx }) => {
  const [open, setOpen] = useState(false);
  const cargoDetails = cargo?.cargoDetails;
  const name =
    cargoDetails?.customerCargoDetails?.cargoName ||
    cargoDetails?.contractName ||
    "Unknown Cargo";
  const freightRate = cargoDetails?.freightRate ?? 0;
  const status = cargoDetails?.status || "—";
  const allShipments = cargo?.shipments || cargo?.trips || [];
  const shipments = allShipments.filter(hasRoute);
  const shipmentCount = shipments.length;
  const totalQty = shipments.reduce(
    (a, s) => a + (s?.cargoSummary?.totalCargoQty || 0),
    0,
  );
  const totalProfit = shipments.reduce((a, s) => a + (s?.profit || 0), 0);

  const voyageNumberPart = allShipments[0]?.voyageNumber;
  const cargoNumber = voyageNumberPart
    ? splitVoyageNumber(voyageNumberPart).voyage
    : `C${String(cargo?.cargoPrefixId || 0).padStart(6, "0")}`;

  return (
    <>
      <tr
        onClick={() => setOpen((p) => !p)}
        className={`cursor-pointer h-6 hover:bg-gray-50 border-b border-gray-200 ${
          open ? "bg-[#f0f6ff]" : idx % 2 === 0 ? "bg-white" : "bg-[#f8fafc]"
        }`}
      >
        <td
          className="!py-0.5 !px-2 text-center align-middle border-r border-gray-300"
          style={{ width: "25px", minWidth: "25px", maxWidth: "25px" }}
        >
          <FiChevronDown
            size={14}
            className={`transition-transform duration-300 shrink-0 mx-auto ${open ? "rotate-180" : ""}`}
          />
        </td>

        <td className="!py-0.5 !px-2 align-middle whitespace-nowrap border-r border-gray-300">
          <div className="text-[11px] font-medium text-gray-800 tracking-tight truncate max-w-[180px]">
            {name}
          </div>
        </td>

        <td className="!py-0.5 !px-2 text-center align-middle whitespace-nowrap border-r border-gray-300">
          <span className="text-[11px] font-medium text-gray-800 tracking-tight">
            {cargoNumber}
          </span>
        </td>

        <td className="!py-0.5 !px-2 text-center align-middle whitespace-nowrap border-r border-gray-300">
          {shipmentCount > 0 ? (
            <span className="text-[11px] font-medium text-gray-800 tabular-nums">
              {shipmentCount}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 text-right align-middle whitespace-nowrap border-r border-gray-300">
          {totalQty > 0 ? (
            <span className="text-[11px] font-medium text-gray-800 tabular-nums">
              {totalQty.toLocaleString()}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 text-right align-middle whitespace-nowrap border-r border-gray-300">
          {freightRate > 0 ? (
            <span className="text-[11px] font-medium text-gray-800 tabular-nums">
              {formatCurrency(freightRate)}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 text-right align-middle whitespace-nowrap border-r border-gray-300">
          {totalProfit !== 0 ? (
            <span
              className={`text-[11px] font-medium tabular-nums tracking-tight ${
                totalProfit < 0 ? "text-rose-600" : "text-emerald-600"
              }`}
            >
              {formatCurrency(totalProfit)}
            </span>
          ) : (
            <span className="text-[11px] font-medium text-gray-400">—</span>
          )}
        </td>

        <td className="!py-0.5 !px-2 text-center align-middle whitespace-nowrap">
          <span className="text-[11px] font-medium text-gray-800 capitalize">
            {status}
          </span>
        </td>
      </tr>

      {open && (
        <tr className="bg-[#f4f7fa] detail-row-outer">
          <td colSpan={8} className="!p-0 border-none bg-[#f4f7fa]">
            <Collapse in={open} timeout={300} unmountOnExit>
            <div className="bg-[#f4f7fa] overflow-x-auto p-3 md:p-4">
              {shipmentCount === 0 ? (
                <div className="p-3 flex items-center justify-center text-center gap-3 bg-white rounded-none border border-slate-300 shadow-sm">
                  <div className="w-8 h-8 rounded-none bg-slate-100 flex items-center justify-center shrink-0">
                    <FiInbox size={16} className="text-[#000]" />
                  </div>
                  <div>
                    <p className="m-0 text-[11px] text-[#000]">
                      No active routes
                    </p>
                    <p className="m-0 mt-0.5 text-[11px] text-slate-500">
                      {allShipments.length > 0
                        ? `${allShipments.length} shipment(s) recorded, but none have valid route data assigned yet.`
                        : "There are currently no shipments assigned to this cargo contract."}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="rounded-none border border-slate-300 bg-white overflow-hidden shadow-sm">
                  <table className="w-full border-collapse min-w-[850px]">
                    <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                      <tr>
                        {[
                          { label: "Contract Name" },
                          { label: "Shipment Number", align: "text-center" },
                          { label: "Start Port" },
                          { label: "End Port" },
                          { label: "Start Date" },
                          { label: "End Date" },
                          { label: "Contract Type" },
                          { label: "Cargo Qty", align: "text-right" },
                          { label: "Cargo Units", align: "text-center" },
                          { label: "Profit", align: "text-right" },
                          { label: "Status", align: "text-center" },
                          { label: "Actions", align: "text-center" },
                        ].map((h, i) => (
                          <th
                            key={i}
                            className={`!py-0.5 !px-2 ${h.align || "text-left"
                              } text-[11px] font-medium text-[#777777] capitalize tracking-wider whitespace-nowrap ${
                                i < 11 ? "border-r border-gray-300" : ""
                              }`}
                          >
                            {h.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {shipments.map((shipment, idx) => (
                        <TripRow
                          key={shipment?.estimationContractId ?? idx}
                          trip={{ ...shipment, tripIndex: idx }}
                          isLast={idx === shipments.length - 1}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Collapse>
        </td>
      </tr>
      )}
    </>
  );
};

const VoyageList = ({ setLoader, loader }) => {
  const organizationId = getOrganizationId();
  const [loading, setLoading] = useState(false);
  const [mainTab, setMainTab] = useState(0);

  // Vessel list tab state
  const [vesselVoyages, setVesselVoyages] = useState([]);
  const [vesselPagination, setVesselPagination] = useState(initialPagination);
  const [vesselSearch, setVesselSearch] = useState("");
  const [vesselDebouncedSearch, setVesselDebouncedSearch] = useState("");
  const [vesselContractType, setVesselContractType] = useState("");
  const [vesselStatus, setVesselStatus] = useState("");

  // Cargo list tab state
  const [cargoVoyages, setCargoVoyages] = useState([]);
  const [cargoPagination, setCargoPagination] = useState(initialPagination);
  const [cargoSearch, setCargoSearch] = useState("");
  const [cargoDebouncedSearch, setCargoDebouncedSearch] = useState("");
  const [cargoContractType, setCargoContractType] = useState("");
  const [cargoStatus, setCargoStatus] = useState("");
  const [hasFetched, setHasFetched] = useState(false);

  const access = mainTab === 0 ? "private" : "private";

  // Cache refs to track parameters used for the last successful API request on each tab
  const vesselLastParamsRef = useRef(null);
  const cargoLastParamsRef = useRef(null);
  const requestCounterRef = useRef(0);

  // Active getters
  const voyages = mainTab === 0 ? vesselVoyages : cargoVoyages;
  const pagination = mainTab === 0 ? vesselPagination : cargoPagination;
  const search = mainTab === 0 ? vesselSearch : cargoSearch;
  const debouncedSearch = mainTab === 0 ? vesselDebouncedSearch : cargoDebouncedSearch;
  const contractType = mainTab === 0 ? vesselContractType : cargoContractType;
  const status = mainTab === 0 ? vesselStatus : cargoStatus;

  const fetchVoyageList = useCallback(async () => {
    if (!organizationId) return;
    const requestId = ++requestCounterRef.current;

    const currentParams = {
      page: pagination.offset,
      limit: pagination.limit,
      search: debouncedSearch,
      contractType,
      status,
      access,
    };

    const lastParamsRef = mainTab === 0 ? vesselLastParamsRef : cargoLastParamsRef;

    // Cache check: skip API call if current params match last params and we already have loaded data
    if (
      lastParamsRef.current &&
      lastParamsRef.current.page === currentParams.page &&
      lastParamsRef.current.limit === currentParams.limit &&
      lastParamsRef.current.search === currentParams.search &&
      lastParamsRef.current.contractType === currentParams.contractType &&
      lastParamsRef.current.status === currentParams.status &&
      lastParamsRef.current.access === currentParams.access &&
      voyages.length > 0
    ) {
      return;
    }

    try {
      setLoading(true);
      if (setLoader) setLoader(true);
      const apiCall = mainTab === 0 ? getVoyageList : getCargoVoyageList;
      const res = await apiCall(
        { organizationId },
        currentParams,
      );
      if (requestId !== requestCounterRef.current) return;

      if (mainTab === 0) {
        setVesselVoyages(res?.data || []);
        setVesselPagination((p) => ({
          ...p,
          totalItems: res?.totalItems || 0,
          totalPages: res?.totalPages || 0,
        }));
      } else {
        setCargoVoyages(res?.data || []);
        setCargoPagination((p) => ({
          ...p,
          totalItems: res?.totalItems || 0,
          totalPages: res?.totalPages || 0,
        }));
      }
      lastParamsRef.current = currentParams;
    } catch (err) {
      if (requestId !== requestCounterRef.current) return;
      console.error(err);
      if (mainTab === 0) {
        setVesselVoyages([]);
        setVesselPagination((p) => ({ ...p, totalItems: 0, totalPages: 0 }));
      } else {
        setCargoVoyages([]);
        setCargoPagination((p) => ({ ...p, totalItems: 0, totalPages: 0 }));
      }
      lastParamsRef.current = null;
    } finally {
      if (requestId === requestCounterRef.current) {
        setLoading(false);
        if (setLoader) setLoader(false);
        setHasFetched(true);
      }
    }
  }, [
    organizationId,
    mainTab,
    pagination.offset,
    pagination.limit,
    debouncedSearch,
    contractType,
    status,
    voyages.length,
    access,
    setLoader,
  ]);

  // Separate debouncing effects
  useEffect(() => {
    const t = setTimeout(() => setVesselDebouncedSearch(vesselSearch), 450);
    return () => clearTimeout(t);
  }, [vesselSearch]);

  useEffect(() => {
    const t = setTimeout(() => setCargoDebouncedSearch(cargoSearch), 450);
    return () => clearTimeout(t);
  }, [cargoSearch]);

  useEffect(() => {
    fetchVoyageList();
  }, [fetchVoyageList]);

  // Separate pagination resets to avoid resetting pagination when switching tabs
  useEffect(() => {
    setVesselPagination((p) => ({ ...p, offset: 1 }));
  }, [vesselDebouncedSearch, vesselContractType, vesselStatus]);

  useEffect(() => {
    setCargoPagination((p) => ({ ...p, offset: 1 }));
  }, [cargoDebouncedSearch, cargoContractType, cargoStatus]);

  const onChangePagination = (type) => {
    const setPagination = mainTab === 0 ? setVesselPagination : setCargoPagination;
    setPagination((p) => {
      const cur = p.offset;
      const next =
        {
          first: 1,
          dec: Math.max(1, cur - 1),
          inc: Math.min(p.totalPages, cur + 1),
          last: p.totalPages,
        }[type] ?? cur;
      return { ...p, offset: next };
    });
  };

  const stats = useMemo(() => {
    const validTrips = (voyages || []).flatMap((v) =>
      (v?.trips || []).filter(hasRoute),
    );
    return {
      totalTrips: validTrips.length,
      openTrips: validTrips.filter((t) => t?.status === "open").length,
      totalQty: validTrips.reduce(
        (a, t) => a + (t?.cargoSummary?.totalCargoQty || 0),
        0,
      ),
    };
  }, [voyages]);

  const handleClearFilters = () => {
    if (mainTab === 0) {
      setVesselSearch("");
      setVesselContractType("");
      setVesselStatus("");
    } else {
      setCargoSearch("");
      setCargoContractType("");
      setCargoStatus("");
    }
  };

  const tabs = [
    { label: "Vessel List", key: 0, tooltip: "Vessel List" },
    { label: "Cargo List", key: 1, tooltip: "Cargo List" },
  ];

  const hasFilters = contractType || status || debouncedSearch;

  return (
    <div className="w-full text-[#000] mx-auto max-w-[1800px]  min-h-[calc(100vh-110px)]">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between flex-wrap gap-3 mb-3">
        <CustomTabs
          tabs={tabs}
          active={mainTab}
          onChange={(_, v) => setMainTab(v)}
        />
        <div className="flex flex-row flex-wrap items-center gap-2">
          {mainTab === 0 && (
            <TextField
              select
              variant="outlined"
              size="small"
              value={contractType}
              onChange={(e) => setVesselContractType(e.target.value)}
              className="w-full sm:w-auto sm:min-w-[140px]"
              InputProps={{
                className: "rounded-none text-[11px] font-medium h-7 bg-[#f8fafc]",
              }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="" className="text-[11px] font-medium">
                All contracts
              </MenuItem>
              <MenuItem value="vessel-cargo" className="text-[11px] font-medium">
                Vessel · Cargo
              </MenuItem>
              <MenuItem value="vessel-vessel" className="text-[11px] font-medium">
                Vessel · Vessel
              </MenuItem>
            </TextField>
          )}

          {hasFilters && (
            <button
              onClick={handleClearFilters}
              className="flex items-center gap-1 border-none bg-transparent text-[11px] font-medium text-[#1e40af] cursor-pointer px-2 py-1 rounded-none hover:bg-blue-50 transition-colors"
            >
              <FiX size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-300 rounded-none shadow-sm overflow-hidden flex flex-col">
        <div className="w-full overflow-x-auto">
          <table className="universal-table voyage-list-table fit-content-mobile w-full border-collapse md:min-w-[1050px]">
            <thead className="bg-[#103f6b]">
              <tr className="h-6">
                {(mainTab === 0
                  ? [
                      {
                        label: "",
                        align: "text-center",
                        style: {
                          width: "25px",
                          minWidth: "25px",
                          maxWidth: "25px",
                        },
                      },
                      { label: "Vessel Name", align: "text-left" },
                      { label: "Voyage Number", align: "text-center" },
                      { label: "Trips", align: "text-center" },
                      { label: "Total Cargo (MT)", align: "text-right" },
                      { label: "Hire Rate (Per Day)", align: "text-right" },
                      { label: "Profit", align: "text-right" },
                      { label: "GBB", align: "text-right" },
                    ]
                  : [
                      {
                        label: "",
                        align: "text-center",
                        style: {
                          width: "25px",
                          minWidth: "25px",
                          maxWidth: "25px",
                        },
                      },
                      { label: "Cargo Name", align: "text-left" },
                      { label: "Cargo Number", align: "text-center" },
                      { label: "Shipments", align: "text-center" },
                      { label: "Total Cargo (MT)", align: "text-right" },
                      { label: "Freight Rate", align: "text-right" },
                      { label: "Profit", align: "text-right" },
                      { label: "Status", align: "text-center" },
                    ]
                ).map((col, i) => (
                  <th
                    key={i}
                    style={col.style}
                    className={`!py-0.5 !px-2 ${
                      col.align || "text-left"
                    } text-[11px] font-medium text-white border-r border-gray-400 capitalize tracking-wider select-none whitespace-nowrap`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading || loader ? (
                <tr>
                  <td colSpan={8} className="bg-white">
                    <div className="h-[440px] w-full" />
                  </td>
                </tr>
              ) : !voyages?.length && hasFetched ? (
                <tr>
                  <td colSpan={8} className="p-0 border-none">
                    <div className="flex flex-col items-center justify-center py-20 px-4 text-center bg-[#f8fafc]">
                      <div className="w-14 h-14 rounded-2xl bg-[#f0f6ff] flex items-center justify-center mb-4 shadow-sm border border-blue-100">
                        <FaShip size={28} className="text-[#313C4C] opacity-80" />
                      </div>
                      <p className="m-0 text-base text-[#000]">
                        {mainTab === 0
                          ? "No voyages found"
                          : "No cargo contracts found"}
                      </p>
                      <p className="m-0 mt-1.5 text-sm text-[#000] max-w-md">
                        {hasFilters
                          ? "We couldn't find anything matching your current filters. Try adjusting your search criteria."
                          : mainTab === 0
                            ? "There are currently no recorded voyages in this section."
                            : "There are currently no recorded cargo contracts in this section."}
                      </p>
                      {hasFilters && (
                        <button
                          onClick={handleClearFilters}
                          className="mt-6 px-5 py-2.5 rounded-lg border border-[#e2e8f0] bg-white text-[#313C4C] text-[13px] cursor-pointer hover:bg-slate-50 transition-colors shadow-sm"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : mainTab === 0 ? (
                voyages.map((vessel, idx) => {
                  const id =
                    vessel?.vesselPrefixId ?? vessel?.vesselDetails?._id ?? idx;
                  return (
                    <VesselRow
                      key={id}
                      vessel={vessel}
                      showDivider={idx > 0}
                      idx={idx}
                    />
                  );
                })
              ) : (
                voyages.map((cargo, idx) => {
                  const id =
                    cargo?.cargoPrefixId ?? cargo?.cargoDetails?._id ?? idx;
                  return (
                    <CargoRow
                      key={id}
                      cargo={cargo}
                      showDivider={idx > 0}
                      idx={idx}
                    />
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && !loader && pagination.totalItems > pagination.limit && (
          <div className="flex items-center justify-end p-2 border-t border-[#e2e8f0] bg-[#f8fafc]">
            <div className="shrink-0 w-full">
              <CustomPagination
                onChangePagination={onChangePagination}
                filters={pagination}
                totalPage={pagination.totalItems}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoyageList;
